import React, { useState } from "react";
import { FieldArray } from "react-final-form-arrays";
import Grid from "@mui/material/Grid";
import Typography from "@mui/material/Typography";
import Paper from "@mui/material/Paper";
import Box from "@mui/material/Box";
import IconButton from "@mui/material/IconButton";
import Stack from "@mui/material/Stack";
import Tooltip from "@mui/material/Tooltip";
import FormHelperText from "@mui/material/FormHelperText";
import { DragDropContext, Droppable, Draggable } from "@hello-pangea/dnd";
import type {
  DroppableProvided,
  DropResult,
  DraggableProvided,
  DraggableStateSnapshot,
} from "@hello-pangea/dnd";
import AddIcon from "@mui/icons-material/Add";
import EditIcon from "@mui/icons-material/Edit";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import DragIndicatorIcon from "@mui/icons-material/DragIndicator";
import { useForm } from "react-final-form";
import type { CustomFieldsSchemaItem } from "../types";
import { CustomFieldsSchemaDialog } from "./CustomFieldsSchemaDialog";

const DEFAULT_TYPE_OPTIONS: { value: string; label: string }[] = [
  { label: "Текст", value: "TEXT" },
  { label: "Число", value: "NUMBER" },
  { label: "Булево", value: "BOOLEAN" },
  { label: "Дата", value: "DATE" },
  { label: "Выбор", value: "SELECT" },
  { label: "Страна", value: "COUNTRY" },
];

const getTypeLabelMap = (opts: { value: string; label: string }[]) =>
  Object.fromEntries(opts.map((o) => [o.value, o.label]));

export interface CustomFieldsSchemaFieldProps {
  name: string;
  label?: string;
  helperText?: string;
  addText?: string;
  typeOptions?: { value: string; label: string }[];
  disabled?: boolean;
  xs?: number;
  sm?: number;
  md?: number;
  lg?: number;
  xl?: number;
}

export const CustomFieldsSchemaField: React.FC<
  CustomFieldsSchemaFieldProps
> = ({
  name,
  label = "Пользовательские поля",
  helperText,
  addText = "Добавить поле",
  typeOptions = DEFAULT_TYPE_OPTIONS,
  disabled,
  xs = 12,
  sm,
  md,
  lg,
  xl,
}) => {
  const form = useForm();
  const typeLabelMap = getTypeLabelMap(typeOptions);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingIndex, setEditingIndex] = useState<number | null>(null);

  const handleAdd = () => {
    setEditingIndex(null);
    setDialogOpen(true);
  };

  const handleEdit = (index: number) => {
    setEditingIndex(index);
    setDialogOpen(true);
  };

  const handleCloseDialog = () => {
    setDialogOpen(false);
  };

  const handleSave = (item: CustomFieldsSchemaItem) => {
    const current = form.getFieldState(name)?.value as
      | CustomFieldsSchemaItem[]
      | undefined;
    const list = Array.isArray(current) ? [...current] : [];
    if (
      editingIndex !== null &&
      editingIndex >= 0 &&
      editingIndex < list.length
    ) {
      list[editingIndex] = item;
    } else {
      list.push(item);
    }
    form.change(name, list);
  };

  const getInitialValue = (): Partial<CustomFieldsSchemaItem> | null => {
    if (editingIndex === null) return {};
    const current = form.getFieldState(name)?.value as
      | CustomFieldsSchemaItem[]
      | undefined;
    const list = Array.isArray(current) ? current : [];
    const item = list[editingIndex];
    return item ?? {};
  };

  const onDragEnd = (
    result: DropResult,
    fields: { move: (from: number, to: number) => void }
  ) => {
    if (!result.destination) return;
    fields.move(result.source.index, result.destination.index);
  };

  return (
    <Grid
      size={{
        xs,
        sm: sm ?? xs,
        md: md ?? sm ?? xs,
        lg: lg ?? md ?? sm ?? xs,
        xl: xl ?? lg ?? md ?? sm ?? xs,
      }}
    >
      <FieldArray name={name}>
        {({ fields }) => {
          return (
            <div>
              <Paper sx={{ p: 2 }}>
                <Stack
                  direction="row"
                  justifyContent="space-between"
                  alignItems="center"
                >
                  <Typography variant="subtitle1" fontWeight={500}>
                    {label}
                  </Typography>

                  <Tooltip title={addText}>
                    <IconButton
                      size="small"
                      onClick={handleAdd}
                      disabled={disabled}
                      aria-label={addText}
                    >
                      <AddIcon />
                    </IconButton>
                  </Tooltip>
                </Stack>
              </Paper>
              {helperText && <FormHelperText>{helperText}</FormHelperText>}

              <Stack sx={{ mt: 1 }} spacing={1}>
                <DragDropContext
                  onDragEnd={(result) => onDragEnd(result, fields)}
                >
                  <Droppable
                    droppableId="custom-fields-schema-droppable"
                    direction="vertical"
                  >
                    {(provided: DroppableProvided) => (
                      <Stack
                        ref={provided.innerRef}
                        {...provided.droppableProps}
                        spacing={1}
                      >
                        {fields.map((fieldName: string, index: number) => {
                          const item = (fields.value ?? [])[
                            index
                          ] as CustomFieldsSchemaItem;
                          return (
                            <Draggable
                              key={fieldName}
                              index={index}
                              draggableId={fieldName}
                              isDragDisabled={disabled}
                            >
                              {(
                                provided: DraggableProvided,
                                snapshot: DraggableStateSnapshot
                              ) => (
                                <Paper
                                  ref={provided.innerRef}
                                  {...provided.draggableProps}
                                  sx={{
                                    display: "flex",
                                    alignItems: "center",
                                    justifyContent: "space-between",
                                    gap: 1,
                                    p: 2,
                                    border: snapshot.isDragging
                                      ? "2px solid"
                                      : "none",
                                    borderColor: "primary.main",
                                  }}
                                >
                                  <Box
                                    {...provided.dragHandleProps}
                                    sx={{
                                      display: "flex",
                                      alignItems: "center",
                                      cursor: disabled ? "default" : "grab",
                                      color: "action.active",
                                    }}
                                    title="Перетащите для изменения порядка"
                                  >
                                    <DragIndicatorIcon fontSize="small" />
                                  </Box>
                                  <Box sx={{ flex: 1, minWidth: 0 }}>
                                    <Typography
                                      variant="body2"
                                      fontWeight={500}
                                      noWrap
                                    >
                                      {item?.label || item?.name}
                                      {item?.required ? "*" : null}
                                    </Typography>
                                    <Typography
                                      variant="body2"
                                      color="text.secondary"
                                    >
                                      {typeLabelMap[item?.type] ?? item?.type}
                                    </Typography>
                                  </Box>
                                  <Stack direction="row" spacing={1}>
                                    <IconButton
                                      size="small"
                                      onClick={() => handleEdit(index)}
                                      disabled={disabled}
                                      aria-label="Редактировать"
                                    >
                                      <EditIcon fontSize="small" />
                                    </IconButton>
                                    <IconButton
                                      size="small"
                                      onClick={() => fields.remove(index)}
                                      disabled={disabled}
                                      aria-label="Удалить"
                                    >
                                      <DeleteOutlineIcon fontSize="small" />
                                    </IconButton>
                                  </Stack>
                                </Paper>
                              )}
                            </Draggable>
                          );
                        })}
                        {provided.placeholder}
                      </Stack>
                    )}
                  </Droppable>
                </DragDropContext>
              </Stack>

              <CustomFieldsSchemaDialog
                initialValue={getInitialValue()}
                typeOptions={typeOptions}
                open={dialogOpen}
                onClose={handleCloseDialog}
                onSave={handleSave}
              />
            </div>
          );
        }}
      </FieldArray>
    </Grid>
  );
};
